import { type SxProps } from '@mui/material';
import clsx from 'clsx';
import React, { useId } from 'react';

import { useIsSSR } from '@/hooks/use-is-ssr.ts';
import { useTimer } from '@/hooks/useTimer.ts';
import { VISUAL_REGRESSION_MASK } from '@/lib/visual-regression.ts';
import type { NoDate } from '@/services/new-utils.ts';
import { intervalToDuration } from '@/utils/interval-to-duration.ts';

export type TimerTheme = {
  backgroundColor: string;
  digitsColor: string;
  labelsColor: string;
};

export type TimerProps = {
  sx?: SxProps;
  date: NoDate;
  theme?: Partial<TimerTheme>;
  className?: string;
};

const defaultTheme: TimerTheme = {
  backgroundColor: '#FDC564',
  digitsColor: '#031839',
  labelsColor: '#354661',
};

export const Timer = (props: TimerProps) => {
  const isSSR = useIsSSR();
  const id = useId();
  const windowKey = `timer_${id}`;
  const theme = { ...defaultTheme, ...props.theme };
  const style: Record<string, string> = {
    '--timer-background-color': theme.backgroundColor,
    '--timer-digit-color': theme.digitsColor,
    '--timer-label-color': theme.labelsColor,
  };
  const timer = useTimer(props.date, () =>
    typeof window !== 'undefined' && isSSR ? (window as any)[windowKey] : new Date(),
  );
  return (
    <div
      id={id}
      className={clsx('flex typography-v1-h3', props.className)}
      style={style}
      {...{ [VISUAL_REGRESSION_MASK]: '' }}
    >
      <Segment value={timer.days} label="dager" />
      <Separator />
      <Segment value={timer.hours} label="timer" />
      <Separator />
      <Segment value={timer.minutes} label="minutter" />
      <Separator />
      <Segment value={timer.seconds} label="sekunder" />
      <script
        dangerouslySetInnerHTML={{
          __html: /* ts */ `
            const now = new Date();
            window['${windowKey}'] = now.toISOString();
            const intervalToDuration = ${intervalToDuration.toString()};
            const d = intervalToDuration({ start: now, end: '${props.date.toISOString()}' });
            const digits = document.querySelectorAll('#${id} .timer-digit');
            const days = d.days.toString().padStart(2, '0');
            const hours = d.hours.toString().padStart(2, '0');
            const minutes = d.minutes.toString().padStart(2, '0');
            const seconds = d.seconds.toString().padStart(2, '0');
            digits[0].innerHTML = days[0];
            digits[1].innerHTML = days[1];
            digits[2].innerHTML = hours[0];
            digits[3].innerHTML = hours[1];
            digits[4].innerHTML = minutes[0];
            digits[5].innerHTML = minutes[1];
            digits[6].innerHTML = seconds[0];
            digits[7].innerHTML = seconds[1];
          `,
        }}
      />
    </div>
  );
};

const Separator = () => {
  return <div className="inline-block px-0.75 py-1 text-(--timer-background-color)">:</div>;
};

type SegmentProps = {
  value: number;
  label: string;
};

const Segment: React.FC<SegmentProps> = ({ value, label }) => {
  const digits = `${value}`.padStart(2, '0').split('');

  return (
    <div className="flex flex-col items-center">
      <div>
        {digits.map((digit, index) => (
          <div
            key={index}
            className="timer-digit inline-grid place-items-center rounded-sm bg-(--timer-background-color) text-center text-(--timer-digit-color) first:mr-1"
            style={{
              width: 'calc(1ch + 2 * 8px)',
              height: 'calc(1lh + 2 * 6px)',
            }}
          >
            {digit}
          </div>
        ))}
      </div>
      <div className="mt-2 typography-v1-caption1 text-(--timer-label-color)">{label}</div>
    </div>
  );
};
